import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { Button } from '../../../components/Button';
import { listProductCategories, listProductsByCategory, listProducts } from '../../../api/endpoints';
import type { SearchResult } from '../../../types/api';

interface CategoryItem {
  code: string;
  name: string;
}

interface ProductItem {
  id: string;
  name1: string;
  name2: string;
  category?: string;
  name_editable?: boolean;
  cat1?: string;
  cat2?: string;
  cat3?: string;
}

// 直近に選択した分類（分類1〜3）。モーダルは閉じるとアンマウントされるため、
// ここに退避して次回オープン時の初期値に使う（2行目以降で前回分類を引き継ぐ）。
// 画面を開いている間だけ保持し、リロードするとクリアされる。
const lastCategory = { cat1: '', cat2: '', cat3: '' };

// 分類リストのキャッシュ。分類は商品マスタ同期のときしか変わらないため、
// 画面を開いている間は取得を1回で済ませる。特に分類1は商品マスタ全件(5万件超)を
// 走査するため実測で約3秒かかり、モーダルを開くたびに待たされていた。
// 引き継いだ分類が表示されるまで数秒かかり「引き継がれていない」ように見えていた。
const categoryCache = {
  level1: null as CategoryItem[] | null,
  level2: new Map<string, CategoryItem[]>(),
  level3: new Map<string, CategoryItem[]>(),
  products: new Map<string, ProductItem[]>(),
};

interface Props {
  open: boolean;
  candidates: SearchResult[];
  query: string;
  onClose: () => void;
  onSelect: (result: SearchResult, rank: number) => void;
}

export function MatchPanel({ open, candidates, query, onClose, onSelect }: Props) {
  // フリーワード検索
  const [freeQuery, setFreeQuery] = useState('');
  const [freeResults, setFreeResults] = useState<ProductItem[]>([]);
  const [freeSearching, setFreeSearching] = useState(false);
  const [freePage, setFreePage] = useState(1);
  const [freeTotal, setFreeTotal] = useState(0);
  const freePerPage = 20;

  // カスケード分類
  const [cat1List, setCat1List] = useState<CategoryItem[]>(categoryCache.level1 ?? []);
  const [cat2List, setCat2List] = useState<CategoryItem[]>(
    () => categoryCache.level2.get(lastCategory.cat1) ?? [],
  );
  const [cat3List, setCat3List] = useState<CategoryItem[]>(
    () => categoryCache.level3.get(lastCategory.cat2) ?? [],
  );
  const [productList, setProductList] = useState<ProductItem[]>(
    () => categoryCache.products.get(lastCategory.cat3) ?? [],
  );
  const [cat1, setCat1] = useState(lastCategory.cat1);
  const [cat2, setCat2] = useState(lastCategory.cat2);
  const [cat3, setCat3] = useState(lastCategory.cat3);
  const [selectedProduct, setSelectedProduct] = useState('');
  // コピー完了の一時表示（ボタンのラベルを1.5秒だけ切り替える）
  const [copiedCode, setCopiedCode] = useState('');
  const codeInputRef = useRef<HTMLInputElement>(null);

  // 分類1をロード（初回のみ取得。2回目以降はキャッシュから即表示）
  useEffect(() => {
    if (!open || categoryCache.level1) return;
    void (async () => {
      try {
        const data = await listProductCategories(1);
        categoryCache.level1 = data.items;
        setCat1List(data.items);
      } catch { /* silent */ }
    })();
  }, [open]);

  // 分類2をロード（選択のクリアは onChange 側で行う。ここでクリアすると復元値が消える）
  useEffect(() => {
    if (!cat1) { setCat2List([]); return; }
    const cached = categoryCache.level2.get(cat1);
    if (cached) { setCat2List(cached); return; }
    setCat2List([]);
    let cancelled = false;
    void (async () => {
      try {
        const data = await listProductCategories(2, cat1);
        categoryCache.level2.set(cat1, data.items);
        if (!cancelled) setCat2List(data.items);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [cat1]);

  // 分類3をロード
  useEffect(() => {
    if (!cat2) { setCat3List([]); return; }
    const cached = categoryCache.level3.get(cat2);
    if (cached) { setCat3List(cached); return; }
    setCat3List([]);
    let cancelled = false;
    void (async () => {
      try {
        const data = await listProductCategories(3, cat2);
        categoryCache.level3.set(cat2, data.items);
        if (!cancelled) setCat3List(data.items);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [cat2]);

  // 商品一覧をロード
  useEffect(() => {
    if (!cat3) { setProductList([]); return; }
    const cached = categoryCache.products.get(cat3);
    if (cached) { setProductList(cached); return; }
    setProductList([]);
    let cancelled = false;
    void (async () => {
      try {
        const data = await listProductsByCategory(cat3);
        categoryCache.products.set(cat3, data.items);
        if (!cancelled) setProductList(data.items);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [cat3]);

  // 分類の選択変更。下位の分類をクリアしつつ、次回オープン用に退避する。
  function handleCat1Change(value: string) {
    setCat1(value);
    setCat2('');
    setCat3('');
    setSelectedProduct('');
    lastCategory.cat1 = value;
    lastCategory.cat2 = '';
    lastCategory.cat3 = '';
  }

  function handleCat2Change(value: string) {
    setCat2(value);
    setCat3('');
    setSelectedProduct('');
    lastCategory.cat2 = value;
    lastCategory.cat3 = '';
  }

  function handleCat3Change(value: string) {
    setCat3(value);
    setSelectedProduct('');
    lastCategory.cat3 = value;
  }

  // 選択中の商品コードをクリップボードへ。コード直接入力欄へ貼り付ける運用のため
  // （門田氏要望: 商品コードを入力しやすくしたい）。
  // Clipboard API は権限が下りない環境があるため、入力欄を選択する旧方式へ退避する。
  // どちらも失敗した場合は選択状態のままにして、手動でコピーできるようにする。
  // 候補一覧・フリーワード検索から商品を選んだとき、その商品の分類を絞り込み欄へ
  // 反映し、次回オープン時にも引き継げるよう退避する（分類から選んだ場合と揃える）。
  // 分類コードを持たない候補（古い保存結果など）は何もしない。
  function applyCategoryOf(p: { cat1?: string; cat2?: string; cat3?: string }) {
    if (!p.cat1) return;
    setCat1(p.cat1);
    setCat2(p.cat2 || '');
    setCat3(p.cat3 || '');
    lastCategory.cat1 = p.cat1;
    lastCategory.cat2 = p.cat2 || '';
    lastCategory.cat3 = p.cat3 || '';
  }

  async function handleCopyCode() {
    if (!selectedProduct) return;
    const markCopied = () => {
      setCopiedCode(selectedProduct);
      setTimeout(() => setCopiedCode(''), 1500);
    };
    try {
      await navigator.clipboard.writeText(selectedProduct);
      markCopied();
      return;
    } catch { /* 下のフォールバックへ */ }
    const el = codeInputRef.current;
    if (!el) return;
    el.focus();
    el.select();
    try {
      if (document.execCommand('copy')) markCopied();
    } catch { /* 選択状態のままにして手動コピーに委ねる */ }
  }

  async function handleFreeSearch(page = 1) {
    if (!freeQuery.trim()) return;
    setFreeSearching(true);
    try {
      const data = await listProducts({ q: freeQuery.trim(), page, per_page: freePerPage });
      setFreeResults(data.items.map((item: Record<string, string>) => ({
        id: item['商品コード'] || '',
        name1: item['商品名1'] || '',
        name2: item['商品名2'] || '',
        category: item['分類名称3'] || '',
        // 汎用コード判定に使う。落とすとフリーワード検索で選んだ汎用コードが
        // OCR名採用時に仮コードへ倒れてしまう。
        name_editable: item['名称ﾏﾆｭｱﾙ入力区分'] === '1',
        cat1: item['分類コード1'] || '',
        cat2: item['分類コード2'] || '',
        cat3: item['分類コード3'] || '',
      })));
      setFreeTotal(data.total);
      setFreePage(page);
    } catch { /* silent */ }
    setFreeSearching(false);
  }

  function handleCategorySelect() {
    if (!selectedProduct) return;
    const product = productList.find((p) => p.id === selectedProduct);
    if (product) {
      const cat3Name = cat3List.find((c) => c.code === cat3)?.name || '';
      onSelect(
        { id: product.id, name1: product.name1, name2: product.name2, category: cat3Name,
          name_editable: product.name_editable },
        -1,
      );
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`候補から選択: ${query}`} widthClass="max-w-5xl">
      <div className="overflow-auto max-h-[50vh] mb-4">
        <table>
          <thead>
            <tr>
              <th>順位</th>
              <th>商品名</th>
              <th>商品名2</th>
              <th>分類</th>
              <th>商品コード</th>
              <th>在庫</th>
              <th>スコア</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((r, i) => (
              <tr key={r.id}>
                <td className="text-center">{i + 1}</td>
                <td>{r.name1}</td>
                <td className="text-sm text-muted">{r.name2}</td>
                <td className="text-xs text-muted">{r.category}</td>
                <td className="text-xs font-mono text-muted">{r.id}</td>
                <td className="text-xs text-center">
                  {r.stock_status === 'in_stock' ? (
                    <span className="inline-block px-1.5 py-0.5 rounded bg-green-100 text-green-800 cursor-help"
                      title={r.stock_detail ? `ゾーン: ${r.stock_detail.zone} ${r.stock_detail.zone_name}\n発注点数: ${r.stock_detail.order_point}\n最大在庫数: ${r.stock_detail.max_stock}` : ''}>在庫</span>
                  ) : r.stock_status === 'none' ? (
                    <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 cursor-help"
                      title={r.stock_detail?.zone ? `ゾーン: ${r.stock_detail.zone} ${r.stock_detail.zone_name}\n発注点数: ${r.stock_detail.order_point}\n最大在庫数: ${r.stock_detail.max_stock}` : '在庫マスタに未登録'}>手配</span>
                  ) : (
                    <span className="inline-block px-1.5 py-0.5 rounded bg-slate-50 text-slate-400">不明</span>
                  )}
                </td>
                <td
                  className="text-sm"
                  title={import.meta.env.DEV && r.s_text != null
                    ? `text: ${r.s_text.toFixed(3)}\naffinity: ${(r.s_affinity ?? 0).toFixed(3)}\nrecency: ${(r.s_recency ?? 0).toFixed(3)}`
                    : undefined}
                >
                  {(r.score_total ?? r.score ?? 0).toFixed(3)}
                </td>
                <td>
                  <Button onClick={() => { applyCategoryOf(r); onSelect(r, i + 1); }}>選択</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* フリーワード検索 */}
      <div className="bg-slate-50 p-3 rounded mb-3">
        <p className="text-sm font-medium mb-2">フリーワード検索:</p>
        <div className="flex gap-2 items-end">
          <input
            type="text"
            className="border rounded px-2 py-1.5 text-sm flex-1"
            placeholder="商品コード・商品名で検索..."
            value={freeQuery}
            onChange={(e) => setFreeQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void handleFreeSearch(1)}
          />
          <Button onClick={() => void handleFreeSearch(1)} disabled={freeSearching || !freeQuery.trim()}>
            {freeSearching ? '検索中...' : '検索'}
          </Button>
        </div>
        {freeResults.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-muted mb-1">{freeTotal}件中 {(freePage - 1) * freePerPage + 1}〜{Math.min(freePage * freePerPage, freeTotal)}件</p>
            <div className="overflow-auto max-h-[30vh]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-1 pr-2">商品コード</th>
                    <th className="pb-1 pr-2">商品名</th>
                    <th className="pb-1 pr-2">商品名2</th>
                    <th className="pb-1 pr-2">分類</th>
                    <th className="pb-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {freeResults.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-blue-50">
                      <td className="py-1 pr-2 font-mono text-xs">{p.id}</td>
                      <td className="py-1 pr-2">{p.name1}</td>
                      <td className="py-1 pr-2 text-xs text-muted">{p.name2}</td>
                      <td className="py-1 pr-2 text-xs text-muted">{p.category}</td>
                      <td className="py-1">
                        <Button className="text-xs px-2 py-0.5" onClick={() => {
                          // name_editable を落とすと、選んだ汎用コードがOCR名採用時に
                          // 仮コードへ倒れてしまう。項目を詰め直さずそのまま渡す。
                          applyCategoryOf(p);
                          onSelect(p, -1);
                        }}>選択</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {freeTotal > freePerPage && (
              <div className="flex gap-2 items-center mt-2">
                <Button
                  className="text-xs px-2 py-1"
                  disabled={freePage <= 1}
                  onClick={() => void handleFreeSearch(freePage - 1)}
                >前へ</Button>
                <span className="text-xs text-muted">{freePage} / {Math.ceil(freeTotal / freePerPage)}</span>
                <Button
                  className="text-xs px-2 py-1"
                  disabled={freePage >= Math.ceil(freeTotal / freePerPage)}
                  onClick={() => void handleFreeSearch(freePage + 1)}
                >次へ</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 分類からの絞り込み検索 */}
      <div className="bg-slate-50 p-3 rounded mb-3">
        <p className="text-sm font-medium mb-2">分類から選択:</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-muted block mb-1">分類1</label>
            <select
              className="border rounded px-2 py-1.5 text-sm min-w-[140px]"
              value={cat1}
              onChange={(e) => handleCat1Change(e.target.value)}
            >
              <option value="">-- 選択 --</option>
              {cat1List.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">分類2</label>
            <select
              className="border rounded px-2 py-1.5 text-sm min-w-[160px]"
              value={cat2}
              onChange={(e) => handleCat2Change(e.target.value)}
              disabled={!cat1}
            >
              <option value="">-- 選択 --</option>
              {cat2List.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">分類3</label>
            <select
              className="border rounded px-2 py-1.5 text-sm min-w-[180px]"
              value={cat3}
              onChange={(e) => handleCat3Change(e.target.value)}
              disabled={!cat2}
            >
              <option value="">-- 選択 --</option>
              {cat3List.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">
              商品 {productList.length > 0 && `(${productList.length}件)`}
            </label>
            <select
              className="border rounded px-2 py-1.5 text-sm min-w-[220px]"
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              disabled={productList.length === 0}
            >
              <option value="">-- 選択 --</option>
              {productList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name1}{p.name2 ? ` / ${p.name2}` : ''} ({p.id})
                </option>
              ))}
            </select>
          </div>
          <Button onClick={handleCategorySelect} disabled={!selectedProduct}>
            選択
          </Button>
        </div>

        {/* 選択した商品の商品コード。コード直接入力欄へ貼り付けられるよう、
            表示とコピーの両方をここで完結させる。 */}
        <div className="flex items-center gap-2 mt-3">
          <label className="text-xs text-muted">商品コード</label>
          <input
            ref={codeInputRef}
            readOnly
            value={selectedProduct}
            placeholder="商品を選択すると表示されます"
            onFocus={(e) => e.currentTarget.select()}
            className="border rounded px-2 py-1 text-sm font-mono bg-white w-[220px]"
          />
          <Button variant="secondary" onClick={() => void handleCopyCode()} disabled={!selectedProduct}>
            {copiedCode && copiedCode === selectedProduct ? 'コピーしました' : 'コピー'}
          </Button>
        </div>
      </div>

    </Modal>
  );
}
